import { EntityMetadata } from "@ngrx/data";
import { listingID, ListingSummary } from "@recore-solutions/nstar-lib";

type ListingSummaryFilterValues = {
	[property in keyof ListingSummary]?: ListingSummary[property][];
};

export const listingSummaryFilterFn = (listingSummaryFilterValues: ListingSummaryFilterValues) => {
	return (listingSummary: ListingSummary) => {
		return Object.entries(listingSummaryFilterValues || {}).every(
			([key, values]: [keyof ListingSummary, any[]]) => {
				return (Array.isArray(values) ? values : [values]).includes(listingSummary[key]);
			}
		);
	};
};

export const listingSummarySortFn = (listingIDs: ListingSummary["listingID"][]) => {
	return (a: ListingSummary, b: ListingSummary): number => {
		return (listingIDs || []).indexOf(a.listingID) - (listingIDs || []).indexOf(b.listingID);
	};
};

export const listingSummaryMetadata: Partial<EntityMetadata<ListingSummary>> = {
	selectId: (listingSummary: ListingSummary) => listingSummary.listingID,
	filterFn: (entities: ListingSummary[], pattern?: any) => {
		const needles: Partial<ListingSummary>[] = [];
		switch (typeof pattern) {
			case "function":
				return pattern(entities);
			case "string":
			case "number":
				if (pattern !== "") {
					needles.push({ listingID: listingID(pattern) });
				}
				break;
			case "object":
				// If the pattern isn't an array, make it one.
				pattern = Array.isArray(pattern) ? pattern : [pattern];
				// If the pattern isn't an object, make it one.
				pattern = pattern.map((p) => (typeof p === "object" ? p : { listingID: p }));
				needles.push(...pattern);
		}

		const listingSummaryFilterValues: ListingSummaryFilterValues = {};
		// Populate the ListingSummaryFilterValues with the various values of each the needles we've collected.
		// We will use this to find listings that match any of the statuses or listing IDs, etc.
		needles.forEach((needle) => {
			// For each ListingSummary needle, loop over its keys and values.
			Object.entries(needle).forEach(([key, value]) => {
				// Add each value to the list of values to match against.
				listingSummaryFilterValues[key] = listingSummaryFilterValues[key]
					? [...listingSummaryFilterValues[key], value]
					: [value];
			});
		});

		return entities
			.filter(listingSummaryFilterFn(listingSummaryFilterValues))
			.sort(listingSummarySortFn(listingSummaryFilterValues.listingID || []));
	},
};
